import * as React from "react"

import { Button } from "@/components/ui/button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { formatDateShort } from "@/lib/format-date"

import { useSetMarkScopeMutation } from "../api"
import { MARK_SCOPES, SCOPE_LABEL, type EvalMark, type MarkScope } from "../eval-runs-data"

/*
   Where a mark's scope came from, and the admin's control over it. The classifier's call stands
   until an admin decides otherwise; the decision names who made it and keeps the AI's call beside
   it, so an override never erases what it overrode.
*/

const isMarkScope = (v: string): v is MarkScope => MARK_SCOPES.some((s) => s === v)

/** The full provenance, for the selected mark. */
export function ScopeProvenance(props: { mark: EvalMark; method: string | null }) {
  const { mark } = props
  if (mark.scopeSource === "ai") {
    return (
      <p className="mt-1 text-caption text-text-3">
        Determined by AI{props.method ? ` (${props.method})` : ""}
        {mark.scopeReason ? `: ${mark.scopeReason}` : ""}
      </p>
    )
  }
  if (mark.scopeSource === "admin") {
    return (
      <div className="mt-1 text-caption text-text-3">
        <p>
          Edited by {mark.scopeSetByName ?? "an admin"}
          {mark.scopeSetAt ? `, ${formatDateShort(mark.scopeSetAt)}` : ""}
          {mark.scopeReason ? `: ${mark.scopeReason}` : ""}
        </p>
        {mark.aiScope && (
          <p>
            AI said: {SCOPE_LABEL[mark.aiScope]}
            {mark.aiScopeReason ? ` — ${mark.aiScopeReason}` : ""}
          </p>
        )}
      </div>
    )
  }
  return null
}

/** An admin's decision on the mark's scope, or its withdrawal back to the AI's call. */
export function ScopeEditor(props: { runId: number; mark: EvalMark }) {
  const { mark } = props
  const save = useSetMarkScopeMutation(props.runId, mark.id)
  const storedReason = mark.scopeSource === "admin" ? (mark.scopeReason ?? "") : ""
  const [scope, setScope] = React.useState<MarkScope>(mark.scope)
  const [reason, setReason] = React.useState(storedReason)
  const reasonId = React.useId()
  // Saving what is already stored is not a decision: it would relabel the AI's call as an admin's
  // and recompute the run's figures for nothing.
  const changed = scope !== mark.scope || reason.trim() !== storedReason.trim()

  // A refetched mark (after a save, or another admin's) resets the form to what is stored.
  React.useEffect(() => {
    setScope(mark.scope)
    setReason(storedReason)
  }, [mark.scope, storedReason])

  return (
    <form
      className="mt-3 flex flex-col gap-2 border-t border-border-soft pt-3"
      aria-label="Scope of this mark"
      onSubmit={(e) => {
        e.preventDefault()
        if (!changed) return
        save.mutate({ scope, reason: reason.trim() || null })
      }}
    >
      <Select
        value={scope}
        disabled={save.isPending}
        onValueChange={(value) => {
          if (isMarkScope(value)) setScope(value)
        }}
      >
        <SelectTrigger selectSize="sm" className="w-full" aria-label="Scope">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {MARK_SCOPES.map((s) => (
            <SelectItem key={s} value={s}>
              {SCOPE_LABEL[s]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <label htmlFor={reasonId} className="sr-only">
        Reason
      </label>
      <Textarea
        id={reasonId}
        minRows={2}
        maxLength={2000}
        placeholder="Reason (optional)"
        value={reason}
        onChange={(e) => {
          setReason(e.target.value)
        }}
      />
      <p className="text-caption text-text-3">Applies to this mark in every run of the dataset.</p>
      {save.isError && (
        <p className="text-caption text-err">
          The decision could not be saved: {save.error.message}
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        <Button
          type="submit"
          size="sm"
          variant="primary"
          loading={save.isPending}
          disabled={!changed}
        >
          Save scope
        </Button>
        {mark.scopeSource === "admin" && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            disabled={save.isPending}
            onClick={() => {
              save.mutate(null)
            }}
          >
            {/* With no AI call to return to, withdrawing the decision leaves the mark in scope. */}
            {mark.aiScope ? "Revert to AI" : "Withdraw decision"}
          </Button>
        )}
      </div>
    </form>
  )
}
